import { useTranslation } from 'react-i18next';
import { formatAmount } from '../money';
import { claimedKm, formatRate, tripAmount } from './calc';
import type { RatePreviewState } from './use-rate-preview';

/**
 * «Tariffa 0,31 €/km (ACI 2026)   Importo 88,04 €» (spec §2.1): the rate the server would apply and
 * the amount it would compute from the typed km, rounded to the cent. Read-only and informational:
 * the trip freezes its own rate when it is saved.
 */
export function RatePreview({
  state,
  km,
  roundTrip,
}: {
  readonly state: RatePreviewState;
  readonly km: string | null;
  readonly roundTrip: boolean;
}) {
  const { t } = useTranslation('expenses');
  return (
    <div
      data-field="rate"
      role="status"
      className="bg-muted/50 border-border flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 rounded-lg border p-3 text-sm"
    >
      {state.status === 'ready' ? (
        <ReadyRate rate={state.rate} km={km} roundTrip={roundTrip} />
      ) : (
        <span className="text-muted-foreground">{t(`mileage.rate.${state.status}`)}</span>
      )}
    </div>
  );
}

function ReadyRate({
  rate,
  km,
  roundTrip,
}: {
  readonly rate: Extract<RatePreviewState, { status: 'ready' }>['rate'];
  readonly km: string | null;
  readonly roundTrip: boolean;
}) {
  const { t } = useTranslation('expenses');
  const source =
    rate.kind === 'aci'
      ? t('mileage.rate.aci', { year: rate.year ?? '' })
      : t('mileage.rate.fixed');
  return (
    <>
      <span>
        {t('mileage.rate.label')}{' '}
        <b className="tabular-nums">
          {formatRate(rate.eurPerKm)} {t('mileage.rate.unit')}
        </b>{' '}
        <span className="text-muted-foreground">({source})</span>
      </span>
      {km === null ? null : (
        <span>
          {t('mileage.rate.amount')}{' '}
          <b className="tabular-nums">
            {formatAmount(tripAmount(claimedKm(km, roundTrip), rate.eurPerKm))}
          </b>
        </span>
      )}
    </>
  );
}
